import { UserProvider, useUser } from "./components/UserContext";
import LoginPage from "./components/LoginPage";
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme.ts'; // custom theme file
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import HomePage from "./components/HomePage";
import AgentListPage from "./components/AgentListPage";
import AgentDetailPage from "./components/AgentDetailPage";
import NewAgentDetailPage from "./components/NewAgentDetailPage";
import NewAgentListPage from "./components/NewAgentListPage";
import NewAgentPage from "./components/NewAgentPage";
import ConversationalAgentCreator from "./components/ConversationalAgentCreator";
import ConfigPage from "./components/ConfigPage";
import { CodeIndexerPage, DocumentIndexerPage } from "./components/IndexerPages";
import KnowledgeBuilderListPage from "./components/KnowledgeBuilderListPage";
import KnowledgeBuilderDetailPage from "./components/KnowledgeBuilderDetailPage";
import { FeatureFlagProvider, useFeatureFlags } from './context/FeatureFlagContext.tsx';
import Dashboard from "./components/Dashboard.tsx";
import AuditLogsPage from "./components/AuditLogsPage.jsx";
import StepperWizard from "./components/AgentCreate/StepperWizard.tsx";
import AddUserPage from "./components/AddUserPage.tsx";
import ToolCatalog from "./components/Tools/ToolCatalog.tsx";
import ToolDetailPage from "./components/Tools/ToolDetailPage.tsx";
import KeyManager from "./components/KeyManager.tsx";
import WorkflowGeneratorPage from "./components/Workflows/WorkflowGeneratorPage.tsx";
import WorkflowEditPage from "./components/Workflows/WorkflowEditPage.tsx";
import WorkflowDetailPage from "./components/Workflows/WorkflowDetailPage.tsx";
import WorkflowRunPage from "./components/Workflows/WorkflowRunPage.tsx";
import WorkflowListPage from "./components/Workflows/WorkflowListPage.tsx";
import ApprovalsListPage from "./components/Workflows/ApprovalsListPage.tsx";
import ApprovalDetailPage from "./components/Workflows/ApprovalDetailPage.tsx";
import UserListPage from "./components/UserListPage.tsx";
import AgentRunPage from "./components/AgentRunPage.tsx";
import IntentToAgentCreator from "./components/AgentCreate/IntentToAgentCreator.tsx";
import IntentToWorkflowCreator from "./components/Workflows/IntentToWorkflowCreator.tsx";
import './styles.css';

function RequireAuth({ children }) {
  const { user } = useUser();
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

function AppRoutes() {
  const { login } = useUser();
  const { flags } = useFeatureFlags();
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage onLogin={login} />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<RequireAuth><HomePage /></RequireAuth>} />
        <Route path="agent/new" element={<RequireAuth><NewAgentPage /></RequireAuth>} />
        {flags.newAgentPage ? 
          (<>
          <Route path="agent/:agentId" element={<RequireAuth><NewAgentDetailPage /></RequireAuth>} />
          <Route path="agent/:agentId/chatAgent" element={<RequireAuth><AgentRunPage /></RequireAuth>} />
          <Route path="dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
           <Route path="agent/conversational" element={<RequireAuth><StepperWizard /></RequireAuth>} />
           
          <Route path="agents" element={<RequireAuth><NewAgentListPage /></RequireAuth>} />
          <Route path="tools" element={<RequireAuth><ToolCatalog /></RequireAuth>} />
          <Route path="tool/:toolId" element={<RequireAuth><ToolDetailPage /></RequireAuth>} />
          </>) :
          (<>
            <Route path="agent/:agentId" element={<RequireAuth><AgentDetailPage /></RequireAuth>} />
            <Route path="agents" element={<RequireAuth><AgentListPage /></RequireAuth>} />
             <Route path="agent/conversational" element={<ConversationalAgentCreator />} />
          </>)
        }
        <Route path="agent/create" element={<RequireAuth><IntentToAgentCreator /></RequireAuth>} />
        <Route path="agent/conversational" element={<ConversationalAgentCreator />} />
        <Route path="config" element={<RequireAuth><ConfigPage /></RequireAuth>} />
        <Route path="/kb/code-indexer" element={<CodeIndexerPage />} />
        <Route path="/kb/document-indexer" element={<DocumentIndexerPage />} />
        <Route path="/kb" element={<RequireAuth><KnowledgeBuilderListPage /></RequireAuth>} />
        <Route path="/kb/:type/:id" element={<RequireAuth><KnowledgeBuilderDetailPage /></RequireAuth>} />
        <Route path="/audit-logs" element={<RequireAuth><AuditLogsPage /></RequireAuth>} />
        <Route path="/add-user" element={<RequireAuth><AddUserPage /></RequireAuth>} />
        <Route path="/users" element={<RequireAuth><UserListPage /></RequireAuth>} />
        <Route path="/key-manager" element={<RequireAuth><KeyManager /></RequireAuth>} />
        {flags.enableWorkflows && (
          <>
            <Route path="/workflows" element={<RequireAuth><WorkflowListPage /></RequireAuth>} />
            <Route path="/workflow/generate" element={<RequireAuth><WorkflowGeneratorPage /></RequireAuth>} />
            <Route path="/workflow/intent-build" element={<RequireAuth><IntentToWorkflowCreator /></RequireAuth>} />
            <Route path="/workflow/:workflowId/intent-edit" element={<RequireAuth><IntentToWorkflowCreator /></RequireAuth>} />
            <Route path="/workflow/approvals" element={<RequireAuth><ApprovalsListPage /></RequireAuth>} />
            <Route path="/workflow/approvals/:approvalId" element={<RequireAuth><ApprovalDetailPage /></RequireAuth>} />
            <Route path="/workflow/:workflowId/run" element={<RequireAuth><WorkflowRunPage /></RequireAuth>} />
            <Route path="/workflow/:workflowId/edit" element={<RequireAuth><WorkflowEditPage /></RequireAuth>} />
            <Route path="/workflow/:workflowId" element={<RequireAuth><WorkflowDetailPage /></RequireAuth>} />
          </>
        )}
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <FeatureFlagProvider>
    <UserProvider>
        <BrowserRouter>
        <ThemeProvider theme={theme}>
          <AppRoutes />
          </ThemeProvider>
        </BrowserRouter>
    </UserProvider>
   </FeatureFlagProvider>
  );
}